import React from 'react';
import {DatePollInfo} from "../../models/DatePollInfo";
import Calendar, {TileArgs} from "react-calendar";
import UserIconWrapper from "../calendar/UserIconWrapper";
import 'react-calendar/dist/Calendar.css';
import '../../styles/Calendar.scss';
import {Box, Checkbox} from "@mui/material";
import {isSameDate} from "../../utils/Utils";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WeekdaySelector from "../calendar/WeekdaySelector";

interface AlfyPollCalendarProps {
    datePollInfos: DatePollInfo[];
    activeDates: Date[];
    isSelectingAttend?: boolean;
    disabledDates?: Date[]; // 되는 날짜 선택시 안되는 날짜로 선택한 날짜들을 비활성화하기 위한 용도
    selectedAbsentDates: Date[];
    selectedAttendDates: Date[];
    setSelectedAbsentDates: React.Dispatch<React.SetStateAction<Date[]>>;
    setSelectedAttendDates: React.Dispatch<React.SetStateAction<Date[]>>;
}

const AlfyPollCalendar: React.FC<AlfyPollCalendarProps> = (
    {
        datePollInfos,
        activeDates,
        isSelectingAttend = false,
        disabledDates = [],
        selectedAbsentDates,
        selectedAttendDates,
        setSelectedAbsentDates,
        setSelectedAttendDates
    }) => {
    const sortedActiveDates = [...activeDates].sort((a, b) => a.getUTCMilliseconds() - b.getUTCMilliseconds());
    const firstActiveDate = sortedActiveDates[0];
    const lastActiveDate = sortedActiveDates[sortedActiveDates.length - 1];

    const selectedDates = isSelectingAttend ? selectedAttendDates : selectedAbsentDates;
    const setSelectedDates = isSelectingAttend ? setSelectedAttendDates : setSelectedAbsentDates;

    const toggleDate = (targetDate: Date) => {
        const isSelected = selectedDates.find((date) => { return date.getTime() === targetDate.getTime() }) !== undefined;
        if (isSelected) {
            setSelectedDates((prev) => prev.filter((date) => date.getTime() !== targetDate.getTime()));
        } else {
            setSelectedDates((prev) => {
                const updated = [...prev, targetDate];
                return updated.sort((a, b) => (a.getTime() - b.getTime()));
            });
        }
    }

    const getIsDisabled = ({ date }: TileArgs) => {
        const matched = disabledDates.find((item) => {
            return item.getTime() === date.getTime()})

        return matched !== undefined
    }

    return (
        <>
            <Box sx={{bgcolor: 'white', padding: "20px 10px 20px 10px", borderRadius: '8px 8px 0px 0px'}}>
                <span>요일 전체 선택</span>
                <Box height={'10px'}/>
                <WeekdaySelector
                    calendarDates={activeDates}
                    selectedDates={selectedDates}
                    setSelectedDates={setSelectedDates}
                    isSelectingAttend={isSelectingAttend}
                    disabledDates={disabledDates}
                />
            </Box>
            <Calendar
                calendarType="gregory"
                locale="ko-KR"
                defaultValue={firstActiveDate}
                minDate={firstActiveDate}
                maxDate={lastActiveDate}
                minDetail={'month'}
                tileDisabled={getIsDisabled}
                tileContent={({date, view}) => {
                    return (
                        <DaySlot
                            date={date}
                            view={view}
                            pollData={datePollInfos}
                            minDate={firstActiveDate}
                            maxDate={lastActiveDate}
                            isSelectingAttend={isSelectingAttend}
                            disabledDates={disabledDates}
                            selectedAbsentDates={selectedAbsentDates}
                            selectedAttendDates={selectedAttendDates}
                        />
                    )
                }}
                onClickDay={(targetDate, event) => {
                    toggleDate(targetDate);
                }}
                showNeighboringMonth={false}
            />
            <Box height={'20px'} sx={{borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', backgroundColor: 'white'}} />
        </>
    );
}

interface DaySlotProps {
    date: Date;
    view: string;
    pollData: DatePollInfo[];
    minDate: Date;
    maxDate: Date;
    isSelectingAttend?: boolean;
    disabledDates?: Date[];
    selectedAbsentDates?: Date[];
    selectedAttendDates?: Date[];
}

const DaySlot: React.FC<DaySlotProps> = (
    {
        date,
        view,
        pollData,
        minDate,
        maxDate,
        isSelectingAttend = false,
        disabledDates = [],
        selectedAbsentDates = [],
        selectedAttendDates = [],
    }) => {
    const selectedDates = isSelectingAttend ? selectedAttendDates : selectedAbsentDates;

    const isChecked = (targetDate: Date) => {
        return selectedDates.find((date) => { return date.getTime() === targetDate.getTime() }) !== undefined;
    }

    if (date.getTime() > maxDate.getTime() || date.getTime() < minDate.getTime()) { // TODO 범위 제대로 표시 안되는 문제 고쳐야함
        return <></>
    }

    if (view !== 'month') {
        return <></>;
    }

    const datePollInfo = pollData.find((datePollInfo) => isSameDate(datePollInfo.date, date));
    const isDisabledDate = disabledDates.find((disabledDate) => {return disabledDate.getTime() == date.getTime()}) !== undefined;
    return (
        <>
            {isDisabledDate &&
                <Checkbox color={"error"}
                          icon={<RadioButtonUncheckedIcon />}
                          checkedIcon={<CancelIcon />}
                          checked={true}
                          disabled={true}
                />
            }
            {!isDisabledDate &&
                <Checkbox color={isSelectingAttend ? "success" : "error"}
                          icon={<RadioButtonUncheckedIcon />}
                          checkedIcon={isSelectingAttend ? <CheckCircleIcon /> : <CancelIcon />}
                          checked={isChecked(date)}
                />
            }
            {datePollInfo && <UserIconWrapper datePollInfo={datePollInfo} />}
        </>
    );
};

export default AlfyPollCalendar;